// Only the returned allowlist is serialized into browser assets. Never copy process.env.
function httpUrl(value, name, relative = false) {
  if (/[<>\"\'\s]/.test(value)) throw new Error(`${name} contains unsafe URL characters`);
  if (relative && /^\/(?!\/)/.test(value)) {
    if (/[?#]/.test(value)) throw new Error(`${name} must be a base path`);
    return value.replace(/\/$/, '');
  }
  let url;
  try { url = new URL(value); } catch (_) { throw new Error(`${name} must be an HTTP(S) URL`); }
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.search || url.hash) {
    throw new Error(`${name} must be an HTTP(S) URL without credentials, query or fragment`);
  }
  return url.href.replace(/\/$/, '');
}
function createPublicConfig(env = {}) {
  const environment = env.TWADS_ENV || 'development';
  const integrationsMode = env.EXTERNAL_INTEGRATIONS_MODE || 'disabled';
  if (!['development', 'test', 'production'].includes(environment)) throw new Error('Invalid TWADS_ENV');
  if (!['disabled', 'sandbox', 'live'].includes(integrationsMode)) throw new Error('Invalid EXTERNAL_INTEGRATIONS_MODE');
  if (integrationsMode === 'live' && environment !== 'production') throw new Error('Live integrations require production');
  const sandboxOAuthProviders = [...new Set((env.PUBLIC_SANDBOX_OAUTH_PROVIDERS || '').split(',').map(value => value.trim()).filter(Boolean))];
  if (sandboxOAuthProviders.some(provider => provider !== 'twitch')) throw new Error('Sandbox OAuth only supports Twitch');
  if (sandboxOAuthProviders.length && environment === 'production') throw new Error('Sandbox OAuth requires development or test');
  const origin = (value, name) => {
    const normalized = httpUrl(value, name);
    if (new URL(normalized).pathname !== '/') throw new Error(`${name} must be an origin`);
    return normalized;
  };
  const config = {
    environment, integrationsMode, sandboxOAuthProviders,
    integrationsEnabled: integrationsMode !== 'disabled',
    analyticsEnabled: environment === 'production' && integrationsMode === 'live',
    siteUrl: origin(env.PUBLIC_SITE_URL || 'http://localhost:9001', 'PUBLIC_SITE_URL'),
    appUrl: origin(env.PUBLIC_APP_URL || 'http://localhost:9001', 'PUBLIC_APP_URL'),
    apiBaseUrl: httpUrl(env.PUBLIC_API_BASE_URL || '/api', 'PUBLIC_API_BASE_URL', true),
    assetBaseUrl: httpUrl(env.PUBLIC_ASSET_BASE_URL || '/assets', 'PUBLIC_ASSET_BASE_URL', true),
    goodstreamAssetBaseUrl: httpUrl(env.PUBLIC_GOODSTREAM_ASSET_BASE_URL || '/goodstream-assets', 'PUBLIC_GOODSTREAM_ASSET_BASE_URL', true),
    widgetUrl: httpUrl(env.PUBLIC_WIDGET_URL || 'http://localhost:3001', 'PUBLIC_WIDGET_URL'),
    iframeUrl: httpUrl(env.PUBLIC_IFRAME_URL || 'http://localhost:3003', 'PUBLIC_IFRAME_URL'),
    communityUrl: httpUrl(env.PUBLIC_COMMUNITY_URL || 'http://localhost:3003', 'PUBLIC_COMMUNITY_URL'),
    docsUrl: httpUrl(env.PUBLIC_DOCS_URL || 'http://localhost:9001', 'PUBLIC_DOCS_URL'),
    paypalClientId: env.PUBLIC_PAYPAL_CLIENT_ID || '',
    stripePublishableKey: env.PUBLIC_STRIPE_PUBLISHABLE_KEY || '',
    gtmId: env.PUBLIC_GTM_ID || '',
    crispId: env.PUBLIC_CRISP_ID || '',
    amplitudeKey: env.PUBLIC_AMPLITUDE_KEY || '',
    sentryDsn: env.PUBLIC_SENTRY_DSN || '',
    sentryRate: Number(env.PUBLIC_SENTRY_RATE || 0),
    hotjarId: Number(env.PUBLIC_HOTJAR_ID || 0),
    twitterPixelId: env.PUBLIC_TWITTER_PIXEL_ID || '',
    discordServerId: env.PUBLIC_DISCORD_SERVER_ID || '',
    discordChannelId: env.PUBLIC_DISCORD_CHANNEL_ID || '',
    twitterEvents: {subscription: env.PUBLIC_TWITTER_EVENT_SUBSCRIPTION || '', extra: env.PUBLIC_TWITTER_EVENT_EXTRA || '', paymentDialog: env.PUBLIC_TWITTER_EVENT_PAYMENT_DIALOG || '', planDialog: env.PUBLIC_TWITTER_EVENT_PLAN_DIALOG || '', onboarding: env.PUBLIC_TWITTER_EVENT_ONBOARDING || ''},
    supportEmail: env.PUBLIC_SUPPORT_EMAIL || 'support@example.invalid',
  };
  if (config.stripePublishableKey && !config.stripePublishableKey.startsWith('pk_')) throw new Error('Stripe requires a publishable key');
  if (integrationsMode === 'sandbox' && config.stripePublishableKey.startsWith('pk_live_')) throw new Error('Sandbox requires Stripe test key');
  if (!Number.isFinite(config.sentryRate) || config.sentryRate < 0 || config.sentryRate > 1 || !Number.isInteger(config.hotjarId) || config.hotjarId < 0) throw new Error('Invalid public analytics settings');
  return config;
}
function paypalReady(config, available) {
  return config.integrationsEnabled && !!available && !!config.paypalClientId.trim();
}
// Named provider boundaries; local history, invoice and report reads stay available.
const externalEndpoints = {
  GET: [
    /^\/oauth\/[^/]+\/(?:url|login|registration)$/,
    /^\/organization\/[^/]+\/portal$/,
    /^\/organization\/[^/]+\/payments\/(?:oauth\/link|create\/w-form)$/,
  ],
  POST: [
    /^\/topup\/(?:create|[^/]+\/cancel)$/,
    /^\/organization\/[^/]+\/(?:plan\/activate(?:\/paypal)?|plan\/activated\/paypal|extra\/activate|extra\/activated\/paypal|payment(?:-intent)?\/create|cancel-subscription|subscription\/cancel)$/,
    /^\/organization\/[^/]+\/balance\/form\/[^/]+\/withdraw$/,
    /^\/organization\/[^/]+\/stripe\/[^/]+\/recreate$/,
    /^\/organization\/[^/]+\/payments\/form\/[^/]+(?:\/(?:primary|delete|restore|bank-url\/set|moderation\/[^/]+))?$/,
    /^\/payments\/form\/[^/]+\/moderation\/[^/]+$/,
    /^\/payment\/(?:link|unlink)$/,
    /^\/plan\/paypal\/generate$/,
    /^\/email\/send\/(?:test|real)$/,
    /^\/organization\/[^/]+\/email\/[^/]+\/send$/,
  ],
  DELETE: [
    /^\/organization\/[^/]+\/integrations\/[^/]+$/,
    /^\/users\/integrations\/[^/]+$/,
  ],
};
function externalRequestDisabled(path, config, method = 'GET') {
  if (config.integrationsEnabled) return false;
  const pathname = path.split(/[?#]/, 1)[0];
  const oauth = pathname.match(/^\/oauth\/(twitch)\/(?:url|login|registration)$/);
  if (method.toUpperCase() === 'GET' && oauth && (config.sandboxOAuthProviders || []).includes(oauth[1])) return false;
  return (externalEndpoints[method.toUpperCase()] || []).some(pattern => pattern.test(pathname));
}
function disabledIntegrationResponse() {
  const message = 'External integrations are disabled';
  return {code: 503, message, errors: [{code: 'error.externalIntegrationsDisabled', notify: true, message}]};
}
function devServerSettings(env = {}) {
  const port = Number(env.PORT || 9001);
  if (!Number.isInteger(port) || port < 1 || port > 65535) throw new Error('Invalid PORT');
  return {host: env.HOST || 'localhost', port, apiTarget: httpUrl(env.API_BASE_URL || 'http://127.0.0.1:3000', 'API_BASE_URL')};
}
function joinBase(base, path) { return `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`; }
function billingOriginAllowed(candidate, ownOrigin, config) { return [ownOrigin, config.appUrl].includes(candidate) && candidate !== 'null'; }
function mergePublicEnv(settings, config) {
  const analytics = config.analyticsEnabled;
  return {...settings, env: {...settings.env,
    PRODUCTION: config.environment === 'production', DOMAIN: config.siteUrl, APP_DOMAIN: config.appUrl,
    APP_SERVER_DOMAIN: config.apiBaseUrl.replace(/\/api$/, ''), DOCS_DOMAIN: config.docsUrl,
    INTEGRATIONS: config.integrationsEnabled ? settings.env && settings.env.INTEGRATIONS || {} : {KICK: false},
    COMMUNITY_LINK: config.communityUrl,
    TEST_WIDGET_DOMAIN: config.widgetUrl, IFRAME_LINK: config.iframeUrl,
    IFRAME_FOR_WEBSITE: `${config.iframeUrl}/?simulation=true&connection=true`,
    GTM_TOKEN: analytics ? config.gtmId : '', CRISP_TOKEN: analytics ? config.crispId : '',
    AMPLITUDE_ACTIVE: analytics && !!config.amplitudeKey, AMPLITUDE_TOKEN: analytics ? config.amplitudeKey : '',
    SENTRY_DSN: analytics ? config.sentryDsn : '', SENTRY_RATE: analytics ? config.sentryRate : 0,
    SUPPORT_EMAIL: config.supportEmail,
    payments: {...(settings.env && settings.env.payments), paypal: {pk: config.integrationsEnabled ? config.paypalClientId : ''}},
  }};
}
module.exports = {disabledIntegrationResponse, paypalReady, devServerSettings, externalRequestDisabled, createPublicConfig, joinBase, billingOriginAllowed, mergePublicEnv};
